import Vue from 'vue'
import Vuex from 'vuex'
import qrcode from 'qrcode';
import axios  from 'axios';

Vue.use(Vuex);

export function createStore ({
    database,
    uid, controllerUrl,
    videoSource,
    regionCode,
    googleApiKey,
    maxResults = 24,
    exception,
    firestore,
    eventId,
    isLoggedInUser,
    layout,
    access_token,
    projectId,
    httpAgent,
    httpsAgent,
  }) {  
  return new Vuex.Store({
    // IMPORTANT: state must be a function so the module can be
    // instantiated multiple times
    state: () => ({
      videos: undefined,
      qrcode: undefined,
      controllerUrl,
      exception,
      isLoggedInUser,
      videoSource: videoSource || 'recommendation',
      eventId,
      layout,
      settings: undefined,
    }),

    actions: {
      fetchYouTubeVideos ({ commit }) {
        return (async () => {            
          const response = await axios.get('https://www.googleapis.com/youtube/v3/videos', {
            httpAgent,
            httpsAgent,
            params: {
              key: googleApiKey,
              maxResults,
              part: 'snippet',
              chart:'mostPopular',
              regionCode: regionCode || undefined, // deal with empty string
            },
          }).catch((err) => {
            // eslint-disable-next-line no-console
            console.error(`get youtube mostPopular failed with credential ${googleApiKey}`, err.response && err.response.data);
            throw err;
          });
          const videos = response.data.items.map((item) => {
            const { snippet, id } = item;
            return {
              id,
              title: snippet.title,
              created: snippet.publishedAt,
              thumbnail: snippet.thumbnails.high.url || snippet.thumbnails.default.url,
              url: `https://www.youtube.com/watch?v=${id}`,
            };
          });
          if (videos.length == 0) {
            const err = new Error('video not found');
            err.status = 404;
            throw err;
          }
          commit('setVideos', videos);
        })();
      },
      fetchYouTubeLiveVideos ({ commit }) {
        return (async () => {
          const { data: response } = await axios.get(`https://api.ezcast.com/api/portal/livetv`, {
            httpAgent,
            httpsAgent,
            params: {
              region: regionCode,
            },
          });
          const created = new Date(response.date);
          const videos = response.data.map(video => {
            return {
              id: video.id,
              title: video.name,
              thumbnail: video.image,
              url: video.url,
              created,
            };
          });
          if (videos.length == 0) {
            const err = new Error('video not found');
            err.status = 404;
            throw err;
          }
          commit('setVideos', videos.slice(0, maxResults));
        })();
      },
      fetchEzCastVideos ({ commit }) {
        return (async () => {            
          const db = firestore;
          const ezcastVideosSnapshot = await db.collection('ezcast_videos').doc('youtube').get();
          const ezcastVideos = ezcastVideosSnapshot.data();
          const videos = ezcastVideos.videos;
          if (videos.length == 0) {
            const err = new Error('video not found');
            err.status = 404;
            throw err;
          }
          commit('setVideos', videos.slice(0, maxResults));
        })();
      },
      fetchRecommendedVideos ({ commit }) {
        return (async () => {
          const response = await axios.get(`https://us-central1-${projectId}.cloudfunctions.net/usertags/videos`, {
            httpAgent,
            httpsAgent,
            params: {
              access_token,
              limit: maxResults,
            },
          });
          let { videos } = response.data;
          videos = videos.map(video => {
            return {
              id: video.id,
              title: video.title,
              created: video.publishedAt,
              thumbnail: video.thumbnail,
              url: video.page,
            };
          });
          if (videos.length == 0) {
            const err = new Error('video not found');
            err.status = 404;
            throw err;
          }
          commit('setVideos', videos);
        })();
      },
      fetchEventVideos ({ commit }) {
        return (async () => {
          const db = firestore;
          const eventSnapshot = await db.collection('event_splashes').doc(eventId).get();
          const event = eventSnapshot.data();
          const videos = event.videos;
          if (videos.length == 0) {
            const err = new Error('video not found');
            err.status = 404;
            throw err;
          }
          commit('setVideos', videos.slice(0, maxResults));
        })();
      },
      fetchEzNodeHotVideos ({ commit }) {
        return (async () => {
          const response = await axios.get(`https://eznode.ezcast.com/api/videos/hot`, {
            httpAgent,
            httpsAgent,
            params: {
              region: regionCode,
              limit: maxResults,
              idFilter: `^youtube`,
            },
          });
          let { videos } = response.data;
          const getPicture = (video) => {
            if (video.picture) {
              return video.picture;
            }
            if (video.id) {
              let [prefix, youtube_id] = video.id.match(/^youtube_(.*)$/) || [];
              if (youtube_id) {
                return `https://i.ytimg.com/vi/${youtube_id}/hqdefault.jpg`;
              }
            }
          };
          videos = videos.map(video => {
            return {
              id: video.id,
              title: video.title,
              created: video.create_dt,
              thumbnail: getPicture(video),
              url: video.page,
            };
          });
          if (videos.length == 0) {
            const err = new Error('video not found');
            err.status = 404;
            throw err;
          }
          commit('setVideos', videos);
        })();
      },
      async fetchVideos ({ state, commit }) {
        commit('setException', undefined);
        const { videoSource } = state;
        try {
          if (videoSource == 'youtube') {
            await this.dispatch('fetchYouTubeVideos');
          } else if (videoSource == 'youtube_live') {
            await this.dispatch('fetchYouTubeLiveVideos');
          } else if (videoSource == 'youtube_ezcast') {
            await this.dispatch('fetchEzCastVideos');
          } else if (videoSource == 'event') {
            await this.dispatch('fetchEventVideos');
          } else if (videoSource == 'eznode_hot') {
            await this.dispatch('fetchEzNodeHotVideos');
          } else {
            await this.dispatch('fetchRecommendedVideos');
          }
        } catch (err) {
          if (videoSource != 'youtube') {
            await this.dispatch('fetchYouTubeVideos').catch(err => commit('setException', err));
            commit('setVideoSource', 'youtube');
          } else {
            commit('setException', err);
          }
        }
      },
      generateQRCode ({ commit }) {
        return (async () => {
          const code = await qrcode.toDataURL(controllerUrl);
          commit('setQRCode', code);
        })();
      },
      changeVideoSource ({ commit }, videoSource) {
        return (async () => {
          commit('setVideos', undefined);
          commit('setVideoSource', videoSource);
          return this.dispatch('fetchVideos');
        })();
      },
      fetchSettings ({ commit }, did) {
        return (async () => {
          const db = firestore;
          const settingsSnapshot = await db.collection('user_settings').doc(uid).collection('devices').doc(did).get();
          const settings = settingsSnapshot.data() || {};
          commit('updateSettings', settings);
        })();
      },
      changeSettings ({ commit }, { settings, did }) {
        return (async () => {
          const db = firestore;
          await db.collection('user_settings').doc(uid).collection('devices').doc(did).set(settings, { merge: true});
          commit('updateSettings', settings);
        })();
      },
      resetUnreadCount () {
        if (uid) {
          const unreadCountRef = database.ref('notifications/' + uid + '/unreadCount');        
          return unreadCountRef.transaction(function (unreadCount) {
            if (unreadCount != null) {
              return 0;
            }
            return null;
          });  
        }
      },
    },

    mutations: {
      setVideos (state, videos) {
        state.videos = videos;
      },
      setQRCode (state, qrcode) {
        state.qrcode = qrcode;
      },
      setException (state, exception) {
        state.exception = exception;
      },
      setVideoSource (state, videoSource) {
        state.videoSource = videoSource;
      },
      updateSettings (state, settings) {
        state.settings = settings;
      },
    },
  });
}

export let vuex = Vuex;
